import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, UrgentTag, STATUS_META } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/api";
import { Plus } from "lucide-react";

export default function Jobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);

  const filtered = filter === "ALL" ? jobs :
    filter === "ACTIVE" ? jobs.filter((j) => !["COMPLETED", "CANCELLED"].includes(j.status)) :
    jobs.filter((j) => j.status === filter);

  return (
    <div>
      <PageHeader eyebrow={isAdmin ? "All Bench Holders" : "Your Bench"} title={isAdmin ? "Trade Jobs" : "My Trade Jobs"}>
        <button data-testid="new-job-btn" onClick={() => navigate("/jobs/new")} className="btn-brass flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm">
          <Plus size={15} /> New Trade Job
        </button>
      </PageHeader>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {["ALL", "ACTIVE", "SUBMITTED", "IN PROGRESS", "READY FOR COLLECTION", "COMPLETED"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider transition-all border ${
              filter === f ? "border-[#C5A059] text-[#F5F3EF] bg-[#1A1A1E]" : "hairline text-[#8C867A] hover:text-[#E2DFD7]"}`}>
            {STATUS_META[f]?.label || (f === "ALL" ? "All" : f === "ACTIVE" ? "Active" : f)}
          </button>
        ))}
      </div>

      <div className="panel rounded-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b hairline eyebrow">
          <div className="col-span-2">Job No.</div>
          <div className="col-span-4">Piece</div>
          {isAdmin && <div className="col-span-2">Member</div>}
          <div className={isAdmin ? "col-span-2" : "col-span-4"}>Target</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y" style={{ borderColor: "#28282D" }}>
          {filtered.length === 0 && <div className="px-4 py-10 text-center text-sm text-[#8C867A]">No jobs match this filter.</div>}
          {filtered.map((j) => (
            <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}
              className="w-full grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-3 px-4 py-3.5 text-left hover:bg-[#1A1A1E] transition-colors items-center">
              <div className="md:col-span-2 font-mono text-xs brass-text">{j.job_number}</div>
              <div className="md:col-span-4 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#E2DFD7] truncate">{j.title}</span>
                  {j.priority === "URGENT" && <UrgentTag />}
                </div>
                <div className="text-[0.68rem] text-[#8C867A] mt-0.5 truncate">{(j.services || []).slice(0, 3).join(" · ")}</div>
              </div>
              {isAdmin && <div className="md:col-span-2 text-xs text-[#C2BEB5] truncate">{j.member_name}</div>}
              <div className={`${isAdmin ? "md:col-span-2" : "md:col-span-4"} text-xs text-[#8C867A] font-mono`}>{fmtDate(j.target_date)}</div>
              <div className="md:col-span-2 flex md:justify-end col-span-2"><StatusBadge status={j.status} /></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
